import React, { useEffect, useRef, useState } from 'react';

// Pile de profondeur de modale (pour garantir l'ordre même si plusieurs modales sont ouvertes/fermées dans n'importe quel ordre)
const modalStack = [];
let forceUpdateAll = () => {};

/**
 * Modal composant accessible et unifié
 * Props :
 * - open: bool
 * - onClose: function
 * - title: string ou ReactNode
 * - children: contenu
 * - actions: ReactNode (boutons)
 * - initialFocusRef: ref à focus à l'ouverture
 */
const Modal = ({ open, onClose, title, children, actions, initialFocusRef, className = '' }) => {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  // Calcul du z-index dynamique basé sur une pile
  const baseZ = 1000;
  const [_, setRerender] = useState(0);
  const [zIndex, setZIndex] = useState(baseZ);
  const instanceId = useRef(Math.random().toString(36).slice(2));

  // Force tous les modals à se re-render quand la pile change
  forceUpdateAll = () => setRerender(v => v + 1);

  useEffect(() => {
    const id = instanceId.current;
    if (open) {
      if (!modalStack.includes(id)) {
        modalStack.push(id);
        forceUpdateAll();
      }
    } else {
      const idx = modalStack.indexOf(id);
      if (idx !== -1) {
        modalStack.splice(idx, 1);
        forceUpdateAll();
      }
    }
    return () => {
      const idCleanup = id;
      const idx = modalStack.indexOf(idCleanup);
      if (idx !== -1) {
        modalStack.splice(idx, 1);
        forceUpdateAll();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      const idx = modalStack.indexOf(instanceId.current);
      setZIndex(baseZ + (idx + 1) * 10);
    }
  }, [_, open]);

  // Focus trap & ESC
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const focusable = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    // Helper to check if an element is focusable and enabled
    const isFocusable = el => el && typeof el.focus === 'function' && !el.disabled && el.tabIndex !== -1;
    let focusSet = false;
    if (initialFocusRef?.current && isFocusable(initialFocusRef.current)) {
      initialFocusRef.current.focus({ preventScroll: true });
      focusSet = true;
    } else if (focusable.length) {
      for (let i = 0; i < focusable.length; i++) {
        if (isFocusable(focusable[i])) {
          focusable[i].focus({ preventScroll: true });
          focusSet = true;
          break;
        }
      }
    }
    // Si rien n'est focusable, focus sur le conteneur modal
    if (!focusSet && modalRef.current) {
      modalRef.current.focus({ preventScroll: true });
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Tab') {
        // Trap
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus({ preventScroll: true });
    };
  }, [open, onClose, initialFocusRef]);

  // Prevent background scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 ${className}`}
      style={{ zIndex }}
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
      onClick={e => {
        if (e.target === overlayRef.current) onClose();
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      }}
    >
      <div
        ref={modalRef}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6 outline-none ${className}`}
        role="document"
        tabIndex={0}
        aria-labelledby="modal-title"
        style={{ zIndex: zIndex + 1, position: 'relative' }}
      >
        {title && (
          <h2 id="modal-title" className="text-xl font-bold mb-4 text-black dark:text-white">
            {title}
          </h2>
        )}
        <div className="mb-6">{children}</div>
        {actions && <div className="flex gap-3">{actions}</div>}
      </div>
    </div>
  );
};

export default Modal;
